import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Award,
  ShieldCheck,
  CheckCircle2,
  Maximize2,
  X,
  FileCheck,
  ExternalLink,
} from "lucide-react";
import { DpiitLogo, StateEmblemOfIndia } from "../common";

const CERTIFICATES = [
  {
    id: "startup",
    title: "Startup India Certificate of Recognition",
    authority: "Department for Promotion of Industry and Internal Trade (DPIIT)",
    ministry: "Ministry of Commerce & Industry, Government of India",
    image: "/STARTUP.jpg",
    badge: "Govt. of India Recognized",
    badgeColor: "bg-emerald-50 text-[#0B5D4B] border-emerald-200",
    description:
      "Officially recognized by DPIIT under the Startup India initiative, acknowledging Crescet Edustreams as an innovative venture delivering tech-enabled, unified education pathways.",
    highlights: [
      "Official DPIIT recognition for next-gen education delivery",
      "Validates structured cross-vertical ecosystem innovation",
      "Institutional credibility for parents, schools & global universities",
    ],
  },
];

export default function CertificationsSection() {
  const activeTab = CERTIFICATES[0];
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <section className="relative z-10 px-4 py-8 sm:px-6 md:py-12">
      <div className="mx-auto max-w-7xl">
        
        {/* Section Header */}
        <div className="mb-6 text-center sm:mb-8">
          <div className="mb-2.5 inline-flex items-center gap-2 rounded-full border border-[#0B5D4B]/20 bg-white/90 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[#0B5D4B] shadow-sm backdrop-blur">
            <ShieldCheck size={14} className="text-[#0B5D4B]" />
            National Accreditation & Recognition
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Government Recognized Startup
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-xs sm:text-sm leading-relaxed text-slate-600">
            Crescet Edustreams is officially recognized by the Department for Promotion of Industry and Internal Trade (DPIIT),
            Ministry of Commerce & Industry, Government of India under the Startup India initiative.
          </p>
        </div>

        {/* Certificate Card Layout */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_15px_45px_-15px_rgba(15,23,42,0.06)] sm:p-6 lg:p-8">
          
          {/* Main Showcase Grid */}
          <div className="grid items-center gap-6 lg:grid-cols-12 lg:gap-8">
            
            {/* Left Column: Authority & Verification Details (Col 7) */}
            <div className="space-y-4 lg:col-span-7">
              {/* Official DPIIT Government Accreditation Header */}
              <div className="flex items-center pb-3.5 border-b border-slate-100">
                <DpiitLogo />
              </div>

              <div>
                <span className={`inline-block rounded-full border px-3 py-0.5 text-xs font-bold ${activeTab.badgeColor} mb-2`}>
                  {activeTab.badge}
                </span>
                <h3 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                  {activeTab.title}
                </h3>
                <p className="mt-1 text-xs font-semibold text-slate-500">
                  Issued by: {activeTab.authority} · {activeTab.ministry}
                </p>
              </div>

              <p className="text-xs sm:text-sm leading-relaxed text-slate-600">
                {activeTab.description}
              </p>

              {/* Key Verification Points */}
              <div className="space-y-2 rounded-xl bg-slate-50/90 p-3 sm:p-3.5 border border-slate-100">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#0B5D4B]">
                  Verified Compliance & Impact
                </p>
                {activeTab.highlights.map((point) => (
                  <div key={point} className="flex items-start gap-2 text-xs text-slate-700">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#0B5D4B]" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2.5 pt-1">
                <button
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B5D4B] px-5 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-md shadow-[#0B5D4B]/20 hover:bg-[#064E3B] transition-all hover:scale-[1.02] active:scale-95 cursor-pointer"
                >
                  <Maximize2 size={15} />
                  <span>Inspect Official Certificate</span>
                </button>
                <a
                  href={activeTab.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all"
                >
                  <span>Open Full Resolution</span>
                  <ExternalLink size={13} className="text-slate-400" />
                </a>
              </div>
            </div>

            {/* Right Column: Framed Certificate Preview (Col 5) */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div
                onClick={() => setModalOpen(true)}
                className="group relative w-full max-w-xs sm:max-w-sm cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-lg transition-all duration-300 hover:border-[#0B5D4B]/50 hover:shadow-xl hover:scale-[1.01]"
              >
                {/* Certificate Image Frame */}
                <div className="relative h-[270px] sm:h-[310px] w-full overflow-hidden bg-slate-50 flex items-center justify-center p-2">
                  <img
                    src={activeTab.image}
                    alt={activeTab.title}
                    loading="lazy"
                    className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                  />

                  {/* Hover Overlay with Inspect Prompt */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-900/40 opacity-0 backdrop-blur-[2px] transition-opacity duration-300 group-hover:opacity-100">
                    <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-slate-900 shadow-lg">
                      <Maximize2 size={15} className="text-[#0B5D4B]" />
                      <span>Click to Enlarge</span>
                    </div>
                  </div>
                </div>

                {/* Bottom Bar on Certificate Frame */}
                <div className="flex items-center justify-between border-t border-slate-200 bg-white px-3.5 py-2 text-[11px] font-medium text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <FileCheck size={13} className="text-[#0B5D4B]" />
                    <span>Official DPIIT Record</span>
                  </span>
                  <span className="font-semibold text-[#0B5D4B]">Verified Authentic</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* Interactive Lightbox Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setModalOpen(false)}
              className="fixed inset-0 bg-slate-900/80 backdrop-blur-md"
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 15 }}
              transition={{ duration: 0.25 }}
              className="relative z-10 flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/90 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex shrink-0 items-center justify-center p-1.5 bg-white rounded-lg shadow-2xs border border-slate-200">
                    <StateEmblemOfIndia className="h-7 w-auto" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-900">{activeTab.title}</h4>
                    <p className="text-xs text-slate-500">{activeTab.authority} · {activeTab.ministry}</p>
                  </div>
                </div>
                <button
                  onClick={() => setModalOpen(false)}
                  className="rounded-full p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Modal Image View */}
              <div className="flex-1 overflow-auto bg-slate-100 p-4 text-center">
                <img
                  src={activeTab.image}
                  alt={activeTab.title}
                  className="mx-auto max-h-[72vh] w-auto rounded-lg object-contain shadow-md"
                />
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between border-t border-slate-100 bg-white px-6 py-3 text-xs text-slate-500">
                <span>Crescet Edustreams (OPC) Private Limited</span>
                <a
                  href={activeTab.image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-[#0B5D4B] hover:underline"
                >
                  <span>Open Full Resolution Tab</span>
                  <ExternalLink size={13} />
                </a>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
